import React, { useCallback } from "react";
import { TableRowActions } from "../../TableRowActions";
import { useTableRowActionContext } from "@/lib/hooks/ui/useTableRowActionContext";
import { DetailActionProvider } from "@/contexts/DetailActionContext";
import type { BaseEntity, SimpleTableConfig } from "../../types";

export interface TableRowActionsWrapperProps<TData extends BaseEntity> {
  item: TData;
  actions: any[];
  hooks: import("../../types/shared").EntityHooks<TData>;
  entityName: string;
  config?: SimpleTableConfig<TData>;
  compact?: boolean;
  maxVisibleActions?: number;
  /** Optional refetch callback - when provided, used for onRefresh after actions (e.g. embedded tables) */
  refetch?: () => void | Promise<void>;
}

export function TableRowActionsWrapper<TData extends BaseEntity>({
  item,
  actions,
  hooks,
  entityName,
  config,
  compact = true,
  maxVisibleActions = 2,
  refetch: refetchProp,
}: TableRowActionsWrapperProps<TData>) {
  // CRITICAL: Hooks must be called unconditionally and before any early returns
  // Call hooks first, even if we might return null later
  const updateMutation = hooks.useUpdate();
  const deleteMutation = hooks.useDelete();

  // Use provided refetch or no-op - for embedded tables (e.g. company detail contacts) pass refetch
  const refetch = useCallback(() => {
    if (refetchProp) {
      return refetchProp();
    }
    return Promise.resolve();
  }, [refetchProp]);

  // Build action context for this row - hooks are now called at component level
  // When refetchProp provided (embedded table), use it for onDeleteComplete to stay on page
  const rowActionContext = useTableRowActionContext({
    item,
    hooks,
    refetch,
    updateMutation,
    deleteMutation,
    entityName,
    config,
    onDeleteComplete: refetchProp ?? undefined,
  });

  if (!rowActionContext) {
    return null;
  }

  // Wrap in DetailActionProvider so modals can access the context
  return (
    <DetailActionProvider value={rowActionContext}>
      <TableRowActions
        item={item}
        actions={actions}
        actionContext={rowActionContext}
        maxVisibleActions={maxVisibleActions}
        className="w-full"
        compact={compact}
      />
    </DetailActionProvider>
  );
}
